import React, { useState, useEffect, useRef } from "react";
import useToken from "hooks/useToken";
import { useToast } from 'context/providers/ToastProvider';
import { Card, Col, Form, Row, Button, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faFilter, faMapMarkerAlt, faUser, faLaptop, faChair, faCar, faBuilding, faBox } from '@fortawesome/free-solid-svg-icons';
import SoftBadge from 'components/common/SoftBadge';
import { Link } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ModalVisualizarDetallesGasto from '../gastos/modal-registro-gasto/ModalVisualizarDetallesGasto';

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

const ListaActivoFijo = () => {

    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();

    // Estados de Datos
    const [activoFijo, setActivoFijo] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [ubicaciones, setUbicaciones] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Estados de Paginación
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(10);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);

    // Estados de Filtro y Búsqueda
    const [filtroBusqueda, setFiltroBusqueda] = useState('');
    const debouncedFiltroBusqueda = useDebounce(filtroBusqueda, 500);
    const [responsableSeleccionado, setResponsableSeleccionado] = useState(0);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(0);
    const [estadoSeleccionado, setEstadoSeleccionado] = useState("-1");
    const [ubicacionSeleccionada, setUbicacionSeleccionada] = useState("");
    const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

    // Modal de Gasto Vinculado
    const [selectedGasto, setSelectedGasto] = useState(null);
    const [showGastoModal, setShowGastoModal] = useState(false);

    const base64ToFile = (base64String, fileName) => {
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/octet-stream' });
        return new File([blob], fileName, { type: blob.type });
    };

    const ObtenerGastoDetalle = async (id) => {
        var objDataSend = { idGasto: id };

        const response = await fetch("/api/Gastos/ObtenerGastoDetalle", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(objDataSend)
        });

        if (response.ok) {
            const data = await response.json();
            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                let datosGastos = ObjDetalles.table2[0];
                if (datosGastos.vNotaVenta_Name) {
                    var file_Nota = base64ToFile(datosGastos.vNotaVenta_Base64, datosGastos.vNotaVenta_Name);
                    let datoGasto_ = { ...datosGastos };
                    datoGasto_.fileNota = file_Nota;
                    datoGasto_.idTipoMoneda = datosGastos.vMoneda === "MXN" ? 1 : datosGastos.vMoneda === "USD" ? 2 : null;
                    setSelectedGasto(datoGasto_);
                } else {
                    let datoGasto_ = { ...datosGastos };
                    datoGasto_.idTipoMoneda = datosGastos.vMoneda === "MXN" ? 1 : datosGastos.vMoneda === "USD" ? 2 : null;
                    setSelectedGasto(datoGasto_);
                }
                setShowGastoModal(true);
            } else {
                showErrorToast(data.vMessage);
            }
        } else {
            showErrorToast(response.status + ' ' + response.statusText);
        }
    };

    /**
     * Función para obtener la lista de empleados para el filtro de Responsable
     */
    const obtenerListaEmpleados = async () => {
        const response = await fetch("/api/ActivoFijo/ObtenerEmpleados", {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            }
        }).catch((error) => {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error);
        });

        if (response && response.ok) {
            const data = await response.json();
            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                setEmpleados(ObjDetalles.table2 || []);
            } else {
                showErrorToast(data.vMessage);
            }
        } else if (response) {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    };

    /**
     * Función para consultar los activos fijos paginados con filtros
     */
    const mostrarActivoFijo = async (page = 1, append = false) => {
        if (!append) {
            setCargando(true);
        } else {
            setLoadingMore(true);
        }

        const dataToSend = {
            idUsuario: responsableSeleccionado || 0,
            pageNumber: page,
            pageSize: pageSize,
            searchTerm: filtroBusqueda,
            idCategoria: categoriaSeleccionada || 0,
            iEstadoActivo: estadoSeleccionado === "-1" ? -1 : parseInt(estadoSeleccionado),
            vUbicacion: ubicacionSeleccionada
        };

        const response = await fetch("/api/ActivoFijo/ObtenerActivoFijo", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(dataToSend)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error);
        });

        if (response && response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                const nuevosActivos = ObjDetalles.table2 || [];

                if (append) {
                    setActivoFijo(prev => [...prev, ...nuevosActivos]);
                } else {
                    setActivoFijo(nuevosActivos);
                    setPageNumber(1);
                }

                // Obtener total de registros para paginación
                const totalRecordsFromResponse = ObjDetalles.table3?.[0]?.totalRecords || 0;
                setTotalRecords(totalRecordsFromResponse);
                setHasMoreData((page * pageSize) < totalRecordsFromResponse);

                // Obtener ubicaciones únicas
                if (ObjDetalles.table4) {
                    setUbicaciones(ObjDetalles.table4.map(u => u.vUbicacionActivo));
                }
            } else {
                showErrorToast(data.vMessage);
                if (!append) {
                    setActivoFijo([]);
                    setHasMoreData(false);
                }
            }
        } else if (response) {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
            if (!append) {
                setActivoFijo([]);
                setHasMoreData(false);
            }
        }

        if (!append) {
            setCargando(false);
        } else {
            setLoadingMore(false);
        }
    };

    /**
     * Cargar empleados al montar el componente
     */
    useEffect(() => {
        obtenerListaEmpleados();
    }, []);

    /**
     * Efecto con DEBOUNCE para la búsqueda por texto
     */
    useEffect(() => {
        setPageNumber(1);
        setHasMoreData(true);
        mostrarActivoFijo(1, false);
    }, [debouncedFiltroBusqueda]);

    /**
     * Efecto para actualización al cambiar cualquiera de los filtros
     */
    useEffect(() => {
        setPageNumber(1);
        setHasMoreData(true);
        mostrarActivoFijo(1, false);
    }, [responsableSeleccionado, categoriaSeleccionada, estadoSeleccionado, ubicacionSeleccionada]);

    /**
     * Función para cargar la siguiente página (Infinite Scroll)
     */
    const cargarMasDatos = () => {
        if (hasMoreData && !loadingMore && !cargando) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            mostrarActivoFijo(nextPage, true);
        }
    };

    /**
     * Componente para el trigger de infinite scroll mediante IntersectionObserver
     */
    const InfiniteScrollTrigger = () => {
        const triggerRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreData && !loadingMore && !cargando) {
                        cargarMasDatos();
                    }
                },
                { threshold: 0.1 }
            );

            if (triggerRef.current) {
                observer.observe(triggerRef.current);
            }

            return () => {
                if (triggerRef.current) {
                    observer.unobserve(triggerRef.current);
                }
            };
        }, [hasMoreData, loadingMore, cargando]);

        if (!hasMoreData) return null;

        return (
            <div ref={triggerRef} className="text-center py-4">
                {loadingMore ? (
                    <div>
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted small">Cargando más activos fijos...</div>
                    </div>
                ) : (
                    <div className="text-muted small">Desplaza para cargar más</div>
                )}
            </div>
        );
    };

    return (
        <>
            <Card className="mb-3">
                <Card.Header className="pb-0 bg-light">
                    <Row className="align-items-end g-2 mb-3">
                        {/* Búsqueda */}
                        <Col xs={12} md={5}>
                            <Form.Group>
                                <Form.Label className="text-700 mb-1">Buscar activos</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        {filtroBusqueda !== debouncedFiltroBusqueda || cargando ? (
                                            <Spinner animation="border" size="sm" variant="primary" />
                                        ) : (
                                            <FontAwesomeIcon icon={faSearch} />
                                        )}
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Nombre, desc., nº serie..."
                                        value={filtroBusqueda}
                                        onChange={(e) => setFiltroBusqueda(e.target.value)}
                                    />
                                    {filtroBusqueda && (
                                        <Button variant="outline-secondary" onClick={() => setFiltroBusqueda('')}>
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                    )}
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        {/* Botón Filtros Avanzados */}
                        <Col xs={3} md={1}>
                            <Button
                                variant={mostrarFiltrosAvanzados ? 'primary' : 'outline-primary'}
                                onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}
                                className="w-100 mt-4"
                            >
                                <FontAwesomeIcon icon={faFilter} className="me-1" />
                            </Button>
                        </Col>

                        {/* Botón Nuevo Activo Fijo */}
                        <Col xs={9} md={2} className="ms-auto text-end">
                            <Link to="/administracion/activo-fijo/nuevo">
                                <Button
                                    variant="success"
                                    className="d-inline-flex align-items-center gap-2 w-100 justify-content-center mt-4"
                                >
                                    <i className="fas fa-plus" aria-hidden="true"></i>
                                    Nuevo
                                </Button>
                            </Link>
                        </Col>
                    </Row>

                    {/* Filtros Avanzados */}
                    {mostrarFiltrosAvanzados && (
                        <Row className="mt-3 g-2 border-top pt-3 mb-3">
                            {/* Filtro: Responsable */}
                            <Col xs={12} sm={6} md={3}>
                                <Form.Group>
                                    <Form.Label className="text-700 mb-1">Responsable</Form.Label>
                                    <Form.Select
                                        value={responsableSeleccionado}
                                        onChange={(e) => setResponsableSeleccionado(e.target.value)}
                                    >
                                        <option value="0">Todos</option>
                                        {empleados.map((empleado, index) => (
                                            <option key={empleado.idUsuario || index} value={empleado.idUsuario} className={empleado.bIsActive === false ? "text-danger" : ""}>
                                                {empleado.vNombre} {empleado.vApellidos}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Filtro: Categoría */}
                            <Col xs={12} sm={6} md={3}>
                                <Form.Group>
                                    <Form.Label className="text-700 mb-1">Categoría</Form.Label>
                                    <Form.Select
                                        value={categoriaSeleccionada}
                                        onChange={(e) => setCategoriaSeleccionada(e.target.value)}
                                    >
                                        <option value="0">Todas</option>
                                        <option value="1">Electrónica</option>
                                        <option value="2">Mobiliario</option>
                                        <option value="3">Vehículos</option>
                                        <option value="4">Inmuebles</option>
                                        <option value="5">Otros</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Filtro: Estado */}
                            <Col xs={12} sm={6} md={2}>
                                <Form.Group>
                                    <Form.Label className="text-700 mb-1">Estado</Form.Label>
                                    <Form.Select
                                        value={estadoSeleccionado}
                                        onChange={(e) => setEstadoSeleccionado(e.target.value)}
                                    >
                                        <option value="-1">Todos</option>
                                        <option value="1">Activo</option>
                                        <option value="0">Inservible</option>
                                        <option value="2">En reparación</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {/* Filtro: Ubicación */}
                            <Col xs={12} sm={6} md={3}>
                                <Form.Group>
                                    <Form.Label className="text-700 mb-1">Ubicación</Form.Label>
                                    <Form.Select
                                        value={ubicacionSeleccionada}
                                        onChange={(e) => setUbicacionSeleccionada(e.target.value)}
                                    >
                                        <option value="">Todas</option>
                                        {ubicaciones.map((ub, idx) => (
                                            <option key={idx} value={ub}>
                                                {ub}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-2">
                        <Col className="text-end">
                            <small className="text-muted">
                                Mostrando <strong>{activoFijo.length}</strong> de <strong>{totalRecords}</strong> activos fijos
                                {filtroBusqueda && ` para "${filtroBusqueda}"`}
                            </small>
                        </Col>
                    </Row>
                </Card.Header>

                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="p-3">
                            {[...Array(3)].map((_, i) => (
                                <Card key={i} className="mb-3 shadow-sm">
                                    <Card.Body>
                                        <Skeleton height={20} width={100} className="mb-2" />
                                        <Skeleton height={15} width={250} className="mb-2" />
                                        <Skeleton height={15} width={180} />
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    ) : activoFijo.length > 0 ? (
                        <div className="p-3">
                            {activoFijo.map((activo) => {
                                const fechaRegistro = activo.dCreatedDate 
                                    ? new Date(activo.dCreatedDate).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }).replace(/(?:^|\s)([a-z])/g, char => char.toUpperCase()) 
                                    : "N/A";
                                
                                const fechaCompra = activo.dFechaCompra
                                    ? new Date(activo.dFechaCompra).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }).replace(/(?:^|\s)([a-z])/g, char => char.toUpperCase())
                                    : "N/A";

                                const fechaUso = activo.dFechaUso
                                    ? new Date(activo.dFechaUso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }).replace(/(?:^|\s)([a-z])/g, char => char.toUpperCase())
                                    : "N/A";

                                let categoriaName = "Otros";
                                let categoriaIcon = faBox;
                                switch (activo.idCategoria) {
                                    case 1: categoriaName = "Electrónica"; categoriaIcon = faLaptop; break;
                                    case 2: categoriaName = "Mobiliario"; categoriaIcon = faChair; break;
                                    case 3: categoriaName = "Vehículos"; categoriaIcon = faCar; break;
                                    case 4: categoriaName = "Inmuebles"; categoriaIcon = faBuilding; break;
                                    case 5: categoriaName = "Otros"; categoriaIcon = faBox; break;
                                }

                                return (
                                    <Card 
                                        key={activo.idActivoFijo} 
                                        className="mb-3 shadow-sm"
                                        style={{
                                            transition: 'all 0.2s ease-in-out',
                                            borderLeft: '4px solid #2c7be5'
                                        }}
                                    >
                                        <Card.Body>
                                            <Row className="align-items-center">
                                                {/* ID y Fecha */}
                                                <Col xs={12} md={2} className="mb-2 mb-md-0">
                                                    <div className="d-flex align-items-center gap-2 mb-1">
                                                        <h6 className="mb-0">
                                                            <Link
                                                                to={`/administracion/activo-fijo/editar/${activo.idActivoFijo}`}
                                                                className="fw-bold text-decoration-none text-primary"
                                                            >
                                                                #{activo.idActivoFijo}
                                                            </Link>
                                                        </h6>
                                                    </div>
                                                    <div className="mb-1 lh-sm">
                                                        <small className="text-muted d-block mb-1">Reg: <b>{fechaRegistro}</b></small>
                                                        <small className="text-muted d-block mb-1">Compra: <b>{fechaCompra}</b></small>
                                                        <small className="text-muted d-block">Uso: <b>{fechaUso}</b></small>
                                                    </div>
                                                    {activo.idGasto > 0 && (
                                                        <div className="mt-2">
                                                            <span
                                                                className="badge bg-soft-success text-success border border-success-300 cursor-pointer"
                                                                style={{ cursor: 'pointer', fontSize: '0.73rem', padding: '0.35em 0.5em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    ObtenerGastoDetalle(activo.idGasto);
                                                                }}
                                                            >
                                                                <i className="fas fa-link" style={{ fontSize: '0.65rem' }} />
                                                                Gasto Vinculado #{activo.idGasto}
                                                            </span>
                                                        </div>
                                                    )}
                                                </Col>

                                                {/* Información Principal */}
                                                <Col xs={12} md={6} className="mb-2 mb-md-0">
                                                    <div className="mb-2">
                                                        <h6 className="mb-1 text-dark fw-bold">
                                                            {activo.vNombreActivo}
                                                        </h6>
                                                        {activo.vDescripcion && (
                                                            <p className="text-muted small mb-2">{activo.vDescripcion}</p>
                                                        )}
                                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                                            {activo.vNumeroRegistro && (
                                                                <Badge bg="light" text="dark" className="border">
                                                                    Nº Registro: {activo.vNumeroRegistro}
                                                                </Badge>
                                                            )}
                                                            {activo.vNumeroSerie && (
                                                                <Badge bg="light" text="dark" className="border">
                                                                    S/N: {activo.vNumeroSerie}
                                                                </Badge>
                                                            )}
                                                            <span className="text-muted small d-flex align-items-center">
                                                                <FontAwesomeIcon icon={categoriaIcon} className="me-1 text-info" style={{ width: '14px' }} />
                                                                <span className="fw-bold text-dark">{categoriaName}</span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-muted small mb-1">
                                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" style={{ width: '12px' }} />
                                                        <strong>Ubicación:</strong> {activo.vUbicacionActivo || "Sin ubicación"}
                                                    </div>
                                                    <div className="text-muted small">
                                                        <FontAwesomeIcon icon={faUser} className="me-2 text-primary" style={{ width: '12px' }} />
                                                        <strong>Responsable:</strong> {activo.vEmpleadoResponsable || "Sin responsable"}
                                                    </div>
                                                </Col>

                                                {/* Costo y Estado */}
                                                <Col xs={12} md={4} className="text-md-end">
                                                    <div className="mb-2">
                                                        <span className="text-muted d-block small">Costo de Adquisición</span>
                                                        <h4 className="mb-0 fw-bold text-dark">
                                                            {Number(activo.dCostoAdquisicion).toLocaleString("es-MX", {
                                                                style: "currency",
                                                                currency: "MXN"
                                                            })}
                                                        </h4>
                                                    </div>
                                                    <div className="mt-2 d-flex flex-column align-items-end gap-2">
                                                        <SoftBadge
                                                            bg={activo.iEstadoActivo == 1 ? "success" : activo.iEstadoActivo == 0 ? "danger" : "warning"}
                                                        >
                                                            {activo.iEstadoActivo == 1 ? "Activo" : activo.iEstadoActivo == 0 ? "Inservible" : "En reparación"}
                                                        </SoftBadge>
                                                        <Link to={`/administracion/activo-fijo/editar/${activo.idActivoFijo}`}>
                                                            <Button variant="outline-primary" size="sm">
                                                                Ver detalles
                                                            </Button>
                                                        </Link>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                );
                            })}

                            <InfiniteScrollTrigger />
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <h5 className="text-muted">No se encontraron activos fijos</h5>
                            {filtroBusqueda && (
                                <p className="text-muted small">No hay resultados para "{filtroBusqueda}"</p>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {showGastoModal && selectedGasto && (
                <ModalVisualizarDetallesGasto
                    show={showGastoModal}
                    selectedGasto={selectedGasto}
                    onClose={() => {
                        setShowGastoModal(false);
                        setSelectedGasto(null);
                    }}
                />
            )}
        </>
    );

};

export default ListaActivoFijo;